import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronUp } from 'lucide-react';

interface FAQItem {
  question: string;
  answer: string;
}

const faqData: FAQItem[] = [
  {
    question: "What services do you offer?",
    answer: "At Manifest Illusions, we offer a curated suite of services designed to elevate your brand's presence, performance, and perception. Such as Product Ad Photoshoots, Digital Doppelgänger, Brand Scaling & Profit Growth, Logo & Favicon Fingerprints, Conversion Copy Command, and Cinematic Conversions."
  },
  {
    question: "Can I customize which services I need, or do I have to choose a full package?",
    answer: "Absolutely! We believe in tailoring our services to your specific needs. Whether you’re looking for individual services like high-conversion ad copy, video ads, or full-scale brand scaling, you can pick and choose exactly what works for you. No unnecessary packages, just precise, powerful solutions to elevate your brand."
  },
  {
    question: "Do you offer revisions?",
    answer: "Yes, but it depends on the service. For most deliverables like visuals, videos, copywriting, or design assets, we absolutely offer revisions to ensure everything aligns with your brand and expectations. For services that involve deeper strategy such as brand scaling, AI implementation, or business growth planning, we treat it as a collaborative process, not a one-and-done transaction. That means we work closely with you to shape and refine the direction as we go. It’s not about “revisions”, it’s about building something that works."
  },
  {
    question: "What's your pricing structure?",
    answer: "Our pricing varies by service and project complexity. We offer both fixed-price packages and custom quotes for larger projects. Contact us for a detailed quote tailored to your specific needs."
  },
  {
    question: "Do you offer ongoing support or is it a one-time service?",
    answer: "Most of our services are one time. You pay for what you need, when you need it. For long-term brand scaling or deeper growth strategy, ongoing support may be available through a custom agreement, often tied to performance, not monthly billing. Every brand is different, and so is our approach."
  },
  {
    question: "What exactly is Manifest Illusions?",
    answer: "Manifest Illusions is a creative digital services studio specializing in product visuals, AI content, copywriting, and brand growth strategy. We work with businesses and personal brands to enhance how they look, sound, and perform online, with a focus on quality, clarity, and long term impact."
  },
  {
    question: "How is this different from hiring a freelancer or agency?",
    answer: "Manifest Illusions isn’t a freelancer and we’re not a traditional agency either. We’re a specialized digital partner. Freelancers often deliver one-off tasks. Agencies tend to overload you with teams, retainers, and slow moving processes. We offer something different: precision crafted services with the attention of a boutique studio and the execution of a high performance brand team. Everything we deliver is intentional, high-quality, and built to move the needle, not just check a box."
  },
  {
    question: "Is this for businesses, personal brands, or both?",
    answer: "Both, Whether you're an emerging brand, an established business, or a personal identity in the making. Manifest Illusions is designed for those who refuse to blend in. We work with founders, creators, and digital architects who understand that how you present yourself online is not just image, it's influence. Our clients share one mindset: excellence isn't optional."
  }
];

const FAQ: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="faq" className="relative py-20 px-6">
      {/* Luxury Background with Ivory Base and Marble Texture */}
      <div className="absolute inset-0" style={{
        background: 'linear-gradient(135deg, #fefbf3 0%, #faf8f0 25%, #f8f6f0 50%, #f5f3ed 75%, #f2f0ea 100%)',
      }}>
        {/* Marble texture overlay */}
        <div className="absolute inset-0 opacity-15" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='200' height='200' viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23d4af37' fill-opacity='0.08'%3E%3Cpath d='M20 40c0-11.046 8.954-20 20-20s20 8.954 20 20-8.954 20-20 20-20-8.954-20-20zM80 40c0-11.046 8.954-20 20-20s20 8.954 20 20-8.954 20-20 20-20-8.954-20-20zM140 40c0-11.046 8.954-20 20-20s20 8.954 20 20-8.954 20-20 20-20-8.954-20-20zM20 100c0-11.046 8.954-20 20-20s20 8.954 20 20-8.954 20-20 20-20-8.954-20-20zM80 100c0-11.046 8.954-20 20-20s20 8.954 20 20-8.954 20-20 20-20-8.954-20-20zM140 100c0-11.046 8.954-20 20-20s20 8.954 20 20-8.954 20-20 20-20-8.954-20-20zM20 160c0-11.046 8.954-20 20-20s20 8.954 20 20-8.954 20-20 20-20-8.954-20-20zM80 160c0-11.046 8.954-20 20-20s20 8.954 20 20-8.954 20-20 20-20-8.954-20-20zM140 160c0-11.046 8.954-20 20-20s20 8.954 20 20-8.954 20-20 20-20-8.954-20-20z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
        
        {/* Subtle noise texture for richness */}
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.4'/%3E%3C/svg%3E")`,
        }} />
        
        {/* Floating particles - tech elements */}
        {[...Array(15)].map((_, i) => (
          <motion.div
            key={`faq-particle-${i}`}
            className="absolute w-1 h-1 rounded-full opacity-5"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              background: 'linear-gradient(45deg, #d4af37, #f4d03f)',
              boxShadow: '0 0 4px rgba(212, 175, 55, 0.3)'
            }}
            animate={{
              opacity: [0.05, 0.15, 0.05],
              scale: [1, 1.2, 1],
              y: [0, -20, 0],
            }}
            transition={{
              duration: 6 + Math.random() * 4,
              repeat: Infinity,
              delay: Math.random() * 4,
            }}
          />
        ))}

        {/* AI pulse element at top */}
        <motion.div
          className="absolute top-20 left-1/2 transform -translate-x-1/2 w-2 h-2 rounded-full opacity-20"
          style={{
            background: 'radial-gradient(circle, rgba(212, 175, 55, 0.6) 0%, transparent 70%)',
            boxShadow: '0 0 20px rgba(212, 175, 55, 0.3)',
          }}
          animate={{
            scale: [1, 1.5, 1],
            opacity: [0.2, 0.4, 0.2],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      </div>

      <div className="max-w-4xl mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl font-light text-amber-800 mb-4 tracking-wide" style={{ fontFamily: 'Georgia, serif' }}>
            Frequently Asked Questions
          </h2>
          <p className="text-gray-600 text-lg tracking-wide leading-relaxed">
            Uncover the details. Designed for clarity. Engineered with elegance.
          </p>
        </motion.div>

        <div className="space-y-4">
          {faqData.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="backdrop-blur-sm border border-amber-200/30 rounded-lg overflow-hidden shadow-lg"
              style={{
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(252, 248, 227, 0.9) 100%)',
                outline: openIndex === index ? '1px solid rgba(212, 175, 55, 0.4)' : '1px solid transparent',
              }}
            >
              <motion.button
                onClick={() => toggleFAQ(index)}
                className="w-full px-8 py-6 text-left flex items-center justify-between hover:bg-amber-50/30 transition-all duration-300 group"
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
              >
                <span className="text-amber-800 font-medium text-lg pr-4 tracking-wide leading-relaxed" style={{ fontFamily: 'Georgia, serif' }}>
                  {faq.question}
                </span>
                <motion.div
                  animate={{ rotate: openIndex === index ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                  className="flex-shrink-0"
                >
                  <motion.div
                    whileHover={{ scale: 1.1 }}
                    className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center group-hover:bg-amber-200 transition-colors"
                  >
                    <ChevronDown className="w-4 h-4 text-amber-700" />
                  </motion.div>
                </motion.div>
              </motion.button>
              
              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ 
                      duration: 0.4,
                      ease: [0.04, 0.62, 0.23, 0.98]
                    }}
                    className="overflow-hidden"
                  >
                    <motion.div
                      initial={{ y: -10 }}
                      animate={{ y: 0 }}
                      exit={{ y: -10 }}
                      transition={{ duration: 0.3 }}
                      className="px-8 pb-6 pt-2"
                    >
                      <div className="h-px bg-gradient-to-r from-transparent via-amber-300/40 to-transparent mb-4" />
                      <p className="text-gray-700 leading-relaxed tracking-wide whitespace-pre-line" style={{ lineHeight: '1.7' }}>
                        {faq.answer}
                      </p>
                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-center mt-12"
        >
          <p className="text-gray-600 mb-6 tracking-wide">
            Still have questions? We're here to help!
          </p>
          <motion.a
            href="#contact"
            whileHover={{ 
              scale: 1.05,
              boxShadow: "0 10px 30px rgba(212, 175, 55, 0.2)"
            }}
            whileTap={{ scale: 0.95 }}
            className="inline-block text-white px-8 py-3 rounded-lg transition-all duration-300 font-medium tracking-wide"
            style={{
              background: 'linear-gradient(135deg, #d4af37 0%, #f4d03f 100%)',
            }}
          >
            Contact Us
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQ;